import React, { useState, useEffect } from 'react';
import { Clock, Edit, X, Trash2 } from 'lucide-react';

const CompanyAuctionCard = ({ auction, onEditEndTime, onRemove, onCancel, cancellingAuctionId }) => {
  const [timeLeft, setTimeLeft] = useState('');

  useEffect(() => {
    const updateTime = () => {
      const end = new Date(auction.end_time);
      const now = new Date();
      const diff = end - now;
      
      if (diff <= 0) {
        setTimeLeft('Ended');
        return;
      }
      
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      
      setTimeLeft(`${days}d ${hours}h ${minutes}m`);
    };
    
    updateTime();
    const interval = setInterval(updateTime, 60000);
    return () => clearInterval(interval);
  }, [auction.end_time]);

  const currentPrice = parseFloat(auction.current_price) || 0;
  const displayCardId = auction.sample_card_id ?? auction.card_id ?? auction.card?.id;

  return (
    <div className="bg-gray-800 rounded-lg p-6 border border-purple-500/50 hover:border-purple-500 transition-all relative">
      {/* Badge with available count */}
      {auction.availableCount !== undefined && auction.availableCount > 1 && (
        <div className="absolute top-2 right-2 z-10 bg-purple-600 text-white rounded-full w-10 h-10 flex items-center justify-center font-bold text-sm shadow-lg border-2 border-white/50">
          {auction.availableCount}
        </div>
      )}
      <div className="flex items-center justify-between mb-4">
        <span className="px-3 py-1 rounded-full text-xs font-semibold bg-purple-600/30 text-purple-300 border border-purple-500/50">
          🎯 Auction
        </span>
        <div className="flex items-center text-gray-400 text-xs">
          <Clock className="w-4 h-4 mr-1" />
          <span>{timeLeft}</span>
        </div>
      </div>
      
      <h3 className="text-xl font-bold text-white mb-3">
        {auction.player?.full_name || `Card #${auction.card_id}`}
      </h3>
      
      <div className="space-y-2 mb-4">
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-400">Position:</span>
          <span className="text-white font-semibold">{auction.player?.position || 'N/A'}</span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-400">Rarity:</span>
          <span className="text-white font-semibold">
            ⭐ {auction.rarity?.display_name || auction.rarity?.name || 'N/A'}
          </span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-400">Card ID:</span>
          <span className="text-gray-300 font-mono text-xs">#{displayCardId ?? 'N/A'}</span>
        </div>
        <div className="border-t border-gray-700 pt-2 mt-2">
          <div className="flex items-center justify-between">
            <span className="text-gray-400 text-sm">Current Bid:</span>
            <span className="text-purple-400 font-bold text-lg">{currentPrice.toFixed(2)} DIOS</span>
          </div>
          {auction.current_bidder && (
            <div className="flex items-center justify-between mt-1">
              <span className="text-gray-400 text-xs">Current Winner:</span>
              <span className="text-green-400 font-semibold text-xs">
                👑 {auction.current_bidder.username}
              </span>
            </div>
          )}
        </div>
      </div>
      
      <div className="flex space-x-2 mt-4 pt-4 border-t border-gray-700">
        <button
          onClick={() => onEditEndTime(auction)}
          className="flex-1 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg transition-all flex items-center justify-center"
        >
          <Edit className="w-4 h-4 mr-1" />
          Edit End Time
        </button>
        {onCancel && (
          <button
            onClick={() => onCancel(auction.id)}
            disabled={cancellingAuctionId === auction.id}
            className={`px-3 py-2 text-white text-sm font-semibold rounded-lg transition-all flex items-center justify-center ${
              cancellingAuctionId === auction.id
                ? 'bg-orange-800 cursor-not-allowed opacity-50'
                : 'bg-orange-600 hover:bg-orange-700'
            }`}
            title="Cancel Auction (with refund option)"
          >
            <X className="w-4 h-4" />
          </button>
        )}
        <button
          onClick={() => onRemove(auction.id)}
          className="px-3 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded-lg transition-all flex items-center justify-center"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default CompanyAuctionCard;
